import { z } from 'zod';
import { MINUTES_PER_DAY } from '../time';

export const idSchema = z.uuid();

/** Minutes from midnight; the 15-min planning grid is not enforced, odd times are valid. */
export const minutesSchema = z.number().int().min(0).max(MINUTES_PER_DAY);

export const minuteRangeSchema = z
  .object({ fromMin: minutesSchema, toMin: minutesSchema })
  .refine((r) => r.fromMin < r.toMin, { message: 'fromMin must be before toMin' });

/** Optimistic locking: mutations echo the entity's updatedAt; mismatch → 409. */
export const updatedAtSchema = z.iso.datetime({ offset: true });

export const errorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

export type ApiError = z.infer<typeof errorSchema>;

// ---------------------------------------------------------------------------
// Google Sheets CSV import helpers, shared by the volunteer and post parsers.
// ---------------------------------------------------------------------------

/** Header text reduced to what a match should ignore: case and inner spacing. */
export const normHeader = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Value of the column whose header starts with `prefix`, or ''.
 *
 * Prefix matching keeps an import working through the cosmetic header edits a
 * shared spreadsheet accumulates — a trailing note in a title, a changed unit —
 * without asking a coordinator to rename columns before exporting.
 */
export function col(raw: Record<string, string>, prefix: string): string {
  const p = normHeader(prefix);
  for (const [k, v] of Object.entries(raw)) {
    if (normHeader(k).startsWith(p)) return (v ?? '').trim();
  }
  return '';
}

/** One parsed CSV row: the value, a readable reason, or "this row was padding". */
export interface SheetRowResult<T> {
  ok: boolean;
  /** Set when ok. */
  data?: T;
  /** Set when not ok. */
  error?: string;
  /** True for fully empty rows (the sheet pads with blanks) — skip silently. */
  empty?: boolean;
  /**
   * Set when the row holds something real that this import deliberately does
   * not take. Skipped like `empty`, but worth reporting: a coordinator should
   * see what the sheet contained and the importer ignored.
   */
  skipped?: string;
}
